import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, FileSpreadsheet, ListMusic, Plus, ShieldCheck, Tag, Users } from "lucide-react";
import Layout from "@/components/Layout";
import DateRangeFilter, { EMPTY_RANGE, rangeActive, rangeParams } from "@/components/DateRangeFilter";
import { EmptyState, LoadingPanel, StatCard } from "@/components/Bits";
import { Pill } from "@/components/Music";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { api, apiError } from "@/lib/api";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import { cn } from "@/lib/utils";

const SOC_COLOUR = { PUBD: "bg-sec-pub", JAMMS: "bg-sec-pro", BMI: "bg-sec-pro", MLC: "bg-sec-mech", HFA: "bg-sec-mech", MR: "bg-sec-mech" };

function DateBlock({ date }) {
  const d = new Date(`${date}T00:00:00`);
  return (
    <div className="w-11 text-center shrink-0">
      <div className="font-mono text-[10px] tracking-[0.12em] text-muted2">{d.toLocaleDateString(undefined, { month: "short" }).toUpperCase()}</div>
      <div className="font-display text-[20px] font-semibold leading-none">{d.getDate()}</div>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const canEdit = user?.role !== "writer";
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [range, setRange] = useState(EMPTY_RANGE);
  const CK = cacheKey("overview", tenantId);
  const [s, setS] = useState(() => cacheGet(CK) || null);
  const req = useRef(0);

  const load = useCallback(async () => {
    const id = ++req.current;
    try {
      const { data } = await api.get("/stats", { params: rangeParams({ ...range, field: undefined }) });
      if (id !== req.current) return;
      setS(data);
      if (!rangeActive(range)) cacheSet(CK, data);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [range, CK]);

  useEffect(() => {
    load();
  }, [load]);

  if (!s)
    return (
      <Layout title="Overview">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="panel h-[118px] animate-pulse bg-line/30" />
          ))}
        </div>
        <LoadingPanel rows={6} />
      </Layout>
    );

  if (s.catalog_total === 0)
    return (
      <Layout title="Overview">
        <div className="panel">
          <EmptyState
            icon={ListMusic}
            title="Nothing in the catalog yet"
            body="Bring in your spreadsheet — each tab becomes the catalog, your writers or the production board — or add tracks one at a time."
            action={
              canEdit && (
                <div className="flex gap-2 justify-center">
                  <Button asChild variant="outline">
                    <Link to="/catalog/new">
                      <Plus className="w-4 h-4 mr-2" />
                      Add a track
                    </Link>
                  </Button>
                  <Button asChild className="bg-brand hover:bg-brand-hover">
                    <Link to="/import">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Import spreadsheet
                    </Link>
                  </Button>
                </div>
              )
            }
          />
        </div>
      </Layout>
    );

  const maxYear = Math.max(1, ...s.by_year.map((y) => y.count));
  const maxDist = Math.max(1, ...s.by_distributor.map((d) => d.count));
  const thisYear = String(new Date().getFullYear());
  const n = s.total || 1;
  const attention = [
    ...s.dup_examples.map((d) => ({ tone: "bad", title: `ISRC ${d.isrc} is on ${d.titles.length} tracks`, body: d.titles.join(" · "), to: "/catalog?filter=dups", action: "Review" })),
    ...s.split_examples.map((x) => ({ tone: "bad", title: `${x.title} — splits add to ${x.total}%`, body: x.writers.join(" · "), to: `/catalog/${x.track_id}`, action: "Fix split" })),
  ].slice(0, 6);

  return (
    <Layout
      title="Overview"
      subtitle={`${s.catalog_total} tracks · ${s.writers} writers · ${s.by_distributor.length} distributors`}
      action={
        canEdit && (
          <>
            <Button asChild variant="outline" className="hidden md:inline-flex">
              <Link to="/import">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Import
              </Link>
            </Button>
            <Button asChild className="bg-brand hover:bg-brand-hover">
              <Link to="/catalog/new">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Add track</span>
              </Link>
            </Button>
          </>
        )
      }
    >
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <DateRangeFilter value={range} onChange={setRange} />
        <span className="text-[12.5px] text-muted2">
          {rangeActive(range) ? `Counting ${s.total} tracks released in this period` : "Counting the whole catalog — pick dates to narrow it to a release period"}
        </span>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <StatCard label="Tracks" value={s.total} sub={`${s.released} released · ${s.unreleased} not yet out`} icon={ListMusic} onClick={() => navigate("/catalog")} />
        <StatCard label="Missing UPC" value={s.missing_upc} tone={s.missing_upc ? "warn" : "good"} sub={`Release date missing on ${s.missing_date}`} icon={Tag} onClick={() => navigate("/catalog?filter=codes")} />
        <StatCard
          label="Not registered with BMI"
          value={s.total - s.registered.BMI}
          tone={s.total - s.registered.BMI ? "bad" : "good"}
          sub={`${s.total - s.registered.MLC} still open at the MLC`}
          icon={ShieldCheck}
          onClick={() => navigate("/registrations")}
        />
        <StatCard label="Split problems" value={s.split_problems} tone={s.split_problems ? "warn" : "good"} sub="Writer shares that don't add to 100%" icon={Users} onClick={() => navigate("/catalog?filter=splits")} />
      </div>

      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-4 mb-4">
        <section className="panel panel-pad">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="eyebrow">Registration coverage</div>
              <p className="text-[12.5px] text-muted2 mt-1.5">Share of the catalog registered at each society</p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/registrations">
                Open registrations <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Link>
            </Button>
          </div>
          <div className="mt-6 space-y-4">
            {s.societies.map((k) => {
              const v = s.registered[k];
              const pct = Math.round((v / n) * 100);
              return (
                <Link key={k} to={`/registrations?society=${k}`} className="grid grid-cols-[72px_minmax(0,1fr)_96px] items-center gap-3 group">
                  <span className="font-mono text-[12px] font-semibold">{s.society_labels[k]}</span>
                  <span className="h-2.5 rounded-full bg-surface overflow-hidden">
                    <span className={cn("block h-2.5 rounded-full", SOC_COLOUR[k])} style={{ width: `${Math.max(pct, v ? 1 : 0)}%` }} />
                  </span>
                  <span className="font-mono text-[12px] text-muted2 text-right group-hover:text-ink">
                    {v}/{s.total} · {pct}%
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
        <section className="panel p-6 pb-2">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow">Needs attention</div>
            {s.dup_isrc + s.split_problems > 0 && <Pill tone="bad">{s.dup_isrc + s.split_problems} to fix</Pill>}
          </div>
          {attention.length === 0 && <p className="py-6 text-[13px] text-muted2">No duplicate ISRCs or broken splits. Nice.</p>}
          {attention.map((a, i) => (
            <div key={i} className="flex items-center gap-3 py-3 border-t border-line">
              <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", a.tone === "bad" ? "bg-rose-50 dark:bg-rose-950/40" : "bg-gold-light")}>
                <AlertTriangle className={cn("w-4 h-4", a.tone === "bad" ? "text-stock-out" : "text-gold")} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold truncate">{a.title}</div>
                <div className="text-[12px] text-muted2 truncate">{a.body}</div>
              </div>
              <Link to={a.to} className="text-[12.5px] font-semibold text-brand whitespace-nowrap">
                {a.action}
              </Link>
            </div>
          ))}
        </section>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <section className="panel panel-pad">
          <div className="eyebrow">Tracks by distributor</div>
          <div className="mt-4 space-y-2.5">
            {s.by_distributor.slice(0, 8).map((d) => (
              <Link key={d.name} to={`/catalog`} className="grid grid-cols-[112px_minmax(0,1fr)_32px] items-center gap-3">
                <span className="text-[13px] truncate">{d.name}</span>
                <span className="h-5 rounded-md bg-surface">
                  <span className="block h-5 rounded-md bg-sec-dist" style={{ width: `${(d.count / maxDist) * 100}%` }} />
                </span>
                <span className="font-mono text-[12px] text-right">{d.count}</span>
              </Link>
            ))}
            {!s.by_distributor.length && <p className="text-[13px] text-muted2">No distributors recorded.</p>}
          </div>
        </section>
        <section className="panel panel-pad">
          <div className="flex items-start justify-between">
            <div className="eyebrow">Releases by year</div>
            <span className="text-[12px] text-muted2">{s.missing_date} with no date</span>
          </div>
          <div className="flex items-end gap-1.5 h-[190px] mt-4">
            {s.by_year.slice(-10).map((y) => (
              <button
                key={y.year}
                onClick={() => setRange({ preset: "custom", from: `${y.year}-01-01`, to: `${y.year}-12-31`, field: "release_date" })}
                className="flex-1 flex flex-col items-center justify-end gap-1.5 h-full group"
                title={`Show ${y.year}`}
              >
                <span className="font-mono text-[10.5px] text-muted2">{y.count}</span>
                <span className={cn("w-[70%] rounded-t-md group-hover:opacity-80", y.year >= thisYear ? "bg-brand/60" : "bg-brand")} style={{ height: `${Math.max((y.count / maxYear) * 140, 3)}px` }} />
                <span className="font-mono text-[10.5px] text-muted2">{y.year}</span>
              </button>
            ))}
          </div>
        </section>
        <section className="panel p-6 pb-3">
          <div className="flex justify-between mb-2">
            <div className="eyebrow">{s.upcoming.length ? "Coming up" : "Latest releases"}</div>
            <Link to="/releases" className="text-[12.5px] font-semibold text-brand">
              Releases
            </Link>
          </div>
          {(s.upcoming.length ? s.upcoming : s.latest).slice(0, 5).map((t) => (
            <Link key={t.track_id} to={`/catalog/${t.track_id}`} className="flex items-center gap-3 py-2 -mx-2 px-2 rounded-xl hover:bg-brand-light/50">
              <DateBlock date={t.release_date} />
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold truncate">{t.title}</div>
                <div className="text-[12px] text-muted2 truncate">{[t.artist, t.featured && `ft. ${t.featured}`].filter(Boolean).join(" ")}</div>
              </div>
              {t.distributor && <Pill tone="dist">{t.distributor}</Pill>}
            </Link>
          ))}
        </section>
      </div>
    </Layout>
  );
}
